// WHAT: Standard header component for dashboard widgets
// WHY:  Provides consistent header interface with actions (refresh, generate, settings, close)
// DOMAIN: Core → Dashboard → Widgets → Base
// DEPENDENCIES: Lucide icons, cn utility
// RISKS:
//   - NOTE: Action button visibility logic may not match all widget needs
//   - NOTE: Icon and title are both optional - may result in empty header
//   - NOTE: Focus ring styling may not match design system

import { Settings, RefreshCw, X, Sparkles } from "lucide-react";
import { type FC, type ReactNode } from "react";

import { cn } from "@/shared/utils";

/**
 * Props for WidgetHeader component
 */
interface WidgetHeaderProperties {
  /** Additional CSS classes */
  className?: string;
  /** Widget description */
  description?: string;
  /** Widget icon */
  icon?: ReactNode;
  /** Generating state */
  isGenerating?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Close handler */
  onClose?: () => void;
  /** Generate handler (for AI insights) */
  onGenerate?: () => void;
  /** Refresh handler */
  onRefresh?: () => void;
  /** Settings handler */
  onSettings?: () => void;
  /** Widget title */
  title?: string;
}

/**
 * WHAT:
 *   Standard header component that provides consistent interface for all
 *   dashboard widgets with title, description, icon, and action buttons.
 *
 * WHY:
 *   Ensures consistent widget header design and behavior across the dashboard.
 *
 * BEHAVIOR:
 *   - Displays title and description
 *   - Shows icon if provided
 *   - Provides action buttons (generate, refresh, settings, close)
 *   - Handles loading states
 *   - Returns null if no title or icon
 *
 * RISKS:
 *   - NOTE: Action button visibility logic may not match all widget needs
 *   - NOTE: Icon and title are both optional - may result in empty header
 *   - NOTE: Focus ring styling may not match design system
 *
 * @param root0 - Component props
 *
 * @param root0.title - Widget title
 *
 * @param root0.description - Widget description
 *
 * @param root0.icon - Widget icon
 *
 * @param root0.loading - Loading state
 *
 * @param root0.onRefresh - Refresh handler
 *
 * @param root0.onGenerate - Generate handler
 *
 * @param root0.isGenerating - Generating state
 *
 * @param root0.onSettings - Settings handler
 *
 * @param root0.onClose - Close handler
 *
 * @param root0.className - Additional CSS classes
 *
 * @returns {JSX.Element | null} Widget header component or null if no title/icon
 *
 */
export const WidgetHeader: FC<WidgetHeaderProperties> = ({
  className,
  description,
  icon,
  isGenerating = false,
  loading = false,
  onClose,
  onGenerate,
  onRefresh,
  onSettings,
  title,
}) => {
  if (!title && !icon) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between mb-4 pb-3 border-b border-gray-700",
        className,
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {icon ? <div className="flex-shrink-0 text-tracq-bright-blue">{icon}</div> : null}
        <div className="flex-1 min-w-0">
          {title ? <h3 className="text-sm font-semibold text-gray-100 truncate">{title}</h3> : null}
          {description ? (
            <p className="text-xs text-gray-400 truncate mt-0.5">{description}</p>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {onGenerate ? (
          <button
            aria-label="Generate new insights"
            className={cn(
              "p-1.5 rounded-md transition-colors",
              "text-gray-400 hover:text-yellow-400 hover:bg-gray-700/50",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-gray-900",
            )}
            disabled={isGenerating || loading}
            onClick={onGenerate}
            title="Generate new insights"
          >
            <Sparkles className={cn("w-4 h-4", (isGenerating || loading) && "animate-pulse")} />
          </button>
        ) : null}

        {onRefresh ? (
          <button
            aria-label="Refresh widget"
            className={cn(
              "p-1.5 rounded-md transition-colors",
              "text-gray-400 hover:text-gray-200 hover:bg-gray-700/50",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:ring-offset-2 focus:ring-offset-gray-900",
            )}
            disabled={loading}
            onClick={onRefresh}
          >
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          </button>
        ) : null}

        {onSettings ? (
          <button
            aria-label="Widget settings"
            className={cn(
              "p-1.5 rounded-md transition-colors",
              "text-gray-400 hover:text-gray-200 hover:bg-gray-700/50",
              "focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:ring-offset-2 focus:ring-offset-gray-900",
            )}
            onClick={onSettings}
          >
            <Settings className="w-4 h-4" />
          </button>
        ) : null}

        {onClose ? (
          <button
            aria-label="Close widget"
            className={cn(
              "p-1.5 rounded-md transition-colors",
              "text-gray-400 hover:text-gray-200 hover:bg-gray-700/50",
              "focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:ring-offset-2 focus:ring-offset-gray-900",
            )}
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
};
